import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash/isEqual';

import api from 'api';
import { useRequest, useTableState, useFilterState } from 'shared/hooks';

import PaginatedTable from 'shared/components/data/PaginatedTable/PaginatedTable';
import Select from 'shared/components/forms/Select';

import { FlexBoxSpaceBetween, FlexBoxColumn } from 'styles/layout';

import ListFilters from './ListFilters';
import {
  generateTableColumns,
  getTableHeaders,
  prepareTableData,
  transformFacilityRoleData,
} from './permissionsTableUtils';

const userPermissionLabels = {
  Active: 'Active',
  None: 'None',
};

const allowedRoles = [
  'Eeg',
  'EegPediatric',
  'TeleNeurologist',
  'TeleNeuroHospitalist',
];

const UserPermissionsTable = ({ userId, userData, editMode = false, setUpdatedPermissions, reloadTrigger }) => {
  const { control, reset } = useForm({ defaultValues: {} });

  const filteredRoles = useMemo(
    () => (userData.roles || []).filter((role) => allowedRoles.includes(role)),
    [userData.roles],
  );

  const { doRequest, data: listData } = useRequest(
    api.getUserFacilityAccessPermissions,
  );

  const {
    page,
    sortBy,
    sortOrder,
    handlePageChange,
    handleSortChange,
    setPage,
  } = useTableState('Facility', 'asc');
  const { filters, handleFilter } = useFilterState({}, setPage);

  const { parsedData } = useMemo(() => {
    const items = listData?.data || [];
    const transformed = transformFacilityRoleData(items, filteredRoles);
    return { parsedData: transformed };
  }, [listData, filteredRoles]);

  const permissionOptions = useMemo(
    () =>
      Object.entries(userPermissionLabels).map(([value, text]) => ({
        value,
        text,
      })),
    [],
  );

  const defaultValues = useMemo(() => {
    const values = {};
    parsedData.forEach((row) => {
      filteredRoles.forEach((role) => {
        const fieldName = `permission_${row.id}_${role}`;
        const roleValue = row[role];
        values[fieldName] = roleValue === 'n/a' ? 'n/a' : roleValue;
      });
    });
    return values;
  }, [parsedData, filteredRoles]);

  const prevDefaultsRef = useRef({});
  useEffect(() => {
    if (!isEqual(prevDefaultsRef.current, defaultValues)) {
      reset(defaultValues);
      prevDefaultsRef.current = defaultValues;
    }
  }, [defaultValues, reset]);

  // Fetch data
  const fecthList = useCallback(() => {
    doRequest({
      page,
      pageSize: 100,
      filters,
      userId,
      sortBy,
      sortOrder,
    });
  }, [doRequest, page, filters, userId, sortBy, sortOrder]);

  useEffect(() => {
    fecthList();
  }, [fecthList, reloadTrigger])

  const tableColumns = useMemo(
    () => generateTableColumns(filteredRoles),
    [filteredRoles],
  );

  const renderCell = useCallback(
    (value, key, row) => {
      if (!editMode || value === 'n/a' || !filteredRoles.includes(key)) {
        return value;
      }

      const name = `permission_${row.id}_${key}`;

      return (
        <Select
          control={control}
          name={name}
          disabled={false}
          label=""
          placeholder="Select status"
          handleChange={(e) => {
            const selectedValue = e?.target?.value;
            if (!selectedValue || selectedValue === value) return;

            const permissionId = row[`permissionId_${key}`];

            if (!permissionId) return;

            setUpdatedPermissions((prev) => {
              const existing = prev.find((p) => p.id === permissionId);

              if (existing) {
                return prev.map((p) =>
                  p.id === permissionId ? { ...p, status: selectedValue } : p,
                );
              }

              return [...prev, { id: permissionId, status: selectedValue }];
            });
          }}
          options={permissionOptions}
        />
      );
    },
    [
      editMode,
      control,
      filteredRoles,
      permissionOptions,
      setUpdatedPermissions,
    ],
  );

  return (
    <FlexBoxColumn>
      <FlexBoxSpaceBetween>
        <ListFilters onFilterChanged={handleFilter} />
      </FlexBoxSpaceBetween>

      <PaginatedTable
        headings={getTableHeaders(tableColumns)}
        data={prepareTableData(parsedData, tableColumns, renderCell)}
        fallbackMsg="No permissions"
        currentPage={page}
        totalPages={listData?.pageCount}
        onPageChange={handlePageChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
      />
    </FlexBoxColumn>
  );
};

UserPermissionsTable.propTypes = {
  editMode: PropTypes.bool,
  setUpdatedPermissions: PropTypes.func.isRequired,
  reloadTrigger: PropTypes.number,
  userId: PropTypes.string.isRequired,
  userData: PropTypes.shape({
    roles: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

export default UserPermissionsTable;
